import { type ButtonHTMLAttributes, forwardRef } from 'react';
import Icon from './Icon';
import styles from './IconButton.module.css';
import type { IconName } from './icons';

export type IconButtonVariant = 'ghost' | 'subtle' | 'accent' | 'danger';
export type IconButtonSize = 'sm' | 'md' | 'lg';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName;
  /**
   * What the button does, in the words you would use out loud ("Close panel",
   * not "Close"). Required, and not optional by oversight: an icon-only button
   * without one is an unlabelled control, which is silent to a screen reader
   * and ambiguous to everyone else. It becomes the aria-label and the native
   * tooltip.
   */
  label: string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
}

const ICON_SIZE: Record<IconButtonSize, number> = { sm: 14, md: 16, lg: 19 };

/** Forwards its ref for the same reason `Button` does: a menu or popover
    trigger needs the element to put focus back on. */
const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, variant = 'ghost', size = 'md', className, type = 'button', ...rest },
  ref,
) {
  const classes = [styles.button, styles[variant], styles[size], className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <button ref={ref} type={type} className={classes} aria-label={label} title={label} {...rest}>
      <Icon name={icon} size={ICON_SIZE[size]} />
    </button>
  );
});

export default IconButton;
